import React, { useState } from 'react';
import { Trash2, Check, X, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react';
import { AIAgentRule, updateAgentRule, deleteAgentRule, toggleAgentRule } from '../../lib/aiAgents';
import toast from 'react-hot-toast';

interface AgentRuleCardProps {
  rule: AIAgentRule;
  isNew?: boolean;
  onUpdated: () => void;
  onDeleted: () => void;
}

export default function AgentRuleCard({ rule, isNew, onUpdated, onDeleted }: AgentRuleCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editName, setEditName] = useState(rule.name);
  const [editValue, setEditValue] = useState(formatValue(rule.value));
  const [editCondition, setEditCondition] = useState(rule.condition || '');
  const [saving, setSaving] = useState(false);

  function formatValue(val: any): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }

  function parseValue(str: string, valueType: string): any {
    if (!str) return null;
    switch (valueType) {
      case 'currency':
      case 'currency_per_unit':
      case 'percentage':
      case 'number':
        return parseFloat(str) || 0;
      case 'boolean':
        return str.toLowerCase() === 'true';
      case 'list':
        try { return JSON.parse(str); } catch { return str.split(',').map(s => s.trim()); }
      default:
        return str;
    }
  }

  function displayValue(rule: AIAgentRule): string {
    const val = rule.value;
    if (val === null || val === undefined) return '—';

    switch (rule.value_type) {
      case 'currency':
        return `$${Number(val).toFixed(2)}`;
      case 'currency_per_unit':
        return `$${Number(val).toFixed(2)}${rule.unit ? ` / ${rule.unit}` : ''}`;
      case 'percentage':
        return `${val}%`;
      case 'boolean':
        return val ? 'Yes' : 'No';
      case 'list':
        return Array.isArray(val) ? val.join(', ') : String(val);
      default:
        return String(val);
    }
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAgentRule(rule.id, {
        name: editName,
        value: parseValue(editValue, rule.value_type),
        condition: editCondition || null
      });
      setIsEditing(false);
      onUpdated();
    } catch (err) {
      toast.error('Failed to update rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAgentRule(rule.id);
      onDeleted();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggle = async () => {
    try {
      await toggleAgentRule(rule.id, !rule.is_active);
      onUpdated();
    } catch (err) {
      toast.error('Failed to toggle rule');
    }
  };

  return (
    <div className={`border rounded-xl transition-all duration-300 ${
      isNew ? 'border-amber-300 bg-amber-50/50 animate-pulse-once' : 
      rule.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
    }`}>
      <div
        className="px-3.5 py-2.5 flex items-center gap-2 cursor-pointer"
        onClick={() => !isEditing && setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full text-sm font-medium border border-gray-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="text-sm font-medium text-gray-900 truncate">{rule.name}</div>
          )}
          {!isEditing && (
            <div className="text-xs text-gray-500 mt-0.5">{displayValue(rule)}</div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isEditing && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleToggle(); }}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title={rule.is_active ? 'Disable rule' : 'Enable rule'}
              >
                {rule.is_active ? (
                  <ToggleRight className="h-4 w-4 text-green-600" />
                ) : (
                  <ToggleLeft className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </>
          )}
        </div>
      </div>

      {expanded && !isEditing && (
        <div className="px-3.5 pb-3 space-y-2 border-t border-gray-100 pt-2">
          {rule.condition && (
            <div>
              <span className="text-xs font-medium text-gray-500">When: </span>
              <span className="text-xs text-gray-700">{rule.condition}</span>
            </div>
          )}
          {rule.description && (
            <div>
              <span className="text-xs font-medium text-gray-500">Details: </span>
              <span className="text-xs text-gray-700">{rule.description}</span>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-0.5"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="px-3.5 pb-3 space-y-2 border-t border-gray-100 pt-2">
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Value</label>
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Condition (optional)</label>
            <input
              type="text"
              value={editCondition}
              onChange={(e) => setEditCondition(e.target.value)}
              placeholder="e.g., customer lives above 2nd floor"
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              <Check className="h-3 w-3" /> Save
            </button>
            <button
              onClick={() => { setIsEditing(false); setEditName(rule.name); setEditValue(formatValue(rule.value)); setEditCondition(rule.condition || ''); }}
              className="text-xs text-gray-500 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
